import {
	defineComponent,
	ensureLoadingHasResult,
	mergePromises,
	RefQueries,
	useLoadableRef,
	useRouterParam,
	type ReactiveComponent,
} from "vue-utils"

import Api from "@/api"
import { ContentLoading } from "@/components/loading/StateLoading"
import type { MarriageClaimInitialData } from "@/models/marriages/MarriageClaimInitialData"
import { MarriageStageType } from "@/models/marriages/MarriageStageType"
import { css } from "vite-css-in-js"
import BasicPage from "../BasicPage"
import DetailContent from "./DetailContent"

const MarriageDetailsInitializer: ReactiveComponent = () => {
	const claimId = useRouterParam("id", RefQueries.Int)
	const initialData = useLoadableRef<MarriageClaimInitialData | null>(async () => {
		const preloadedData = await mergePromises({
			claim: Api.marriageClaims.nullableGetById(claimId.value),
			exports: Api.marriageClaims.getExports(claimId.value),
			signatureData: Api.marriageClaims.getSignatureVerificationStatus(claimId.value, MarriageStageType.P1),
			partnerSignatureData: Api.marriageClaims.getSignatureVerificationStatus(claimId.value, MarriageStageType.P2),
		})
		if (!preloadedData.claim) {
			return null
		}
		const taxYears = preloadedData.claim.TaxYears.map((year) => year.Year)
		const taxYearBrackets =
			preloadedData.claim.TaxYears.length === 0 ? [] : await Api.marriageClaims.getTaxYearBrackets(taxYears)
		return {
			claim: preloadedData.claim,
			taxYearBrackets,
			exports: preloadedData.exports,
			signatureData: preloadedData.signatureData,
			partnerSignatureData: preloadedData.partnerSignatureData,
		}
	})

	const errorStyling = css`
		display: flex;
		align-items: center;
		justify-content: center;
		height: 100%;
		width: 100%;
	`

	return () => (
		<BasicPage title="Marriage Claim Details" plain>
			<ContentLoading stores={[initialData]}>
				{ensureLoadingHasResult(initialData) !== null ? (
					<DetailContent claimData={ensureLoadingHasResult(initialData)!} />
				) : (
					<div class={errorStyling}>
						<div class="d-flex flex-column align-items-center justify-content-center">
							<h1>Claim Not Found</h1>
							<h5>Could not find a claim with ID: {claimId.value}</h5>
						</div>
					</div>
				)}
			</ContentLoading>
		</BasicPage>
	)
}
export default defineComponent(MarriageDetailsInitializer)
